import type {
  MinimalTaskRun,
  MinimalTaskRunWithRetry,
  Task,
  TaskWithRetry,
} from "./tasks.types";

/** Adds retry property to tasks based on chronological order */
export const addRetryProperty = (
  tasks: MinimalTaskRun[]
): MinimalTaskRunWithRetry[] => {
  const taskNamesSeen: Record<string, MinimalTaskRun> = {};
  const sortedTasks = tasks.toSorted((a, b) =>
    b.start_time.localeCompare(a.start_time)
  );

  // Add "retry" property where needed
  // Since tasks are sorted newest-first (descending), we process them in reverse
  // to find the chronologically oldest (earliest) task for each task_name first
  const tasksWithRetry: MinimalTaskRunWithRetry[] = [];

  for (let i = sortedTasks.length - 1; i >= 0; i--) {
    const task = sortedTasks[i];
    const earliestSeen = taskNamesSeen[task.task_name];

    // A task is a "retry" if there was a chronologically earlier task with the same name
    const isRetry = earliestSeen
      ? task.start_time.localeCompare(earliestSeen.start_time) > 0
      : false;

    // Track the earliest (chronologically oldest) task seen
    if (!earliestSeen) {
      taskNamesSeen[task.task_name] = task;
    }

    tasksWithRetry[i] = { ...task, retry: isRetry };
  }

  return tasksWithRetry;
};

/** Adds retry property to run tasks based on chronological order */
export const addRetryPropertyToRunTasks = (tasks: Task[]): TaskWithRetry[] => {
  const taskNamesSeen = new Set<string>();
  const sortedTasks = tasks.toSorted(
    (a, b) =>
      a.task_name.localeCompare(b.task_name) ||
      a.start_time.localeCompare(b.start_time)
  );

  return sortedTasks.map((task) => {
    if (taskNamesSeen.has(task.task_name)) {
      return { ...task, retry: true };
    }
    taskNamesSeen.add(task.task_name);
    return task;
  });
};
